// 性能优化（FCP/LCP）：本文件曾经只是 utils.ts 的薄包装，
// 任何引用方（landing-main / pwa/init 等 entry 关键路径）都会传递性
// 把整份 utils.ts（2000+ 行 + axios + crypto-js + i18n …）打进 entry chunk。
// 现在改为基于底层 storage 的独立实现，可放心被入口模块同步引用。
//
// 单一来源约束（重要）：
//   utils.ts 中的同名公开方法（getObject / setObject / getTmpObject /
//   setTmpObject / getCookie / setCookie）已经改为薄代理，全部回调到本文件，
//   保证「只有一份实现」。本文件就是这些方法的唯一真实实现，
//   修改时不需要再去同步 utils.ts。
//
// 不在此处重新实现的方法：
//   - clearObject：utils.clearObject 维护实例字段 keepDataKeys，
//     语义高度耦合 Utils 实例，不能在纯函数侧等价复刻 → 留在 utils.ts；
//   - setUser：utils.setUser 还会触发 config.onUser(user) 回调，
//     行为不是单纯的存储写入 → 留在 utils.ts；
//   - getUser：与 utils.getUser 等价但当前没有外部调用方走 storage-helpers，
//     避免新增"看起来等价但不一定一直等价"的 API → 不导出。
import { storage, seStorage } from "./storage";

function getStoreSessionKey(): string | undefined {
  // 真实来源：window._config.storeSessionKey 由后端注入脚本在任何 JS 运行前
  // 设置。utils/config.ts 也声明了 config.storeSessionKey 字段，但当前代码库
  // 中从未对其赋值；如果将来要启用 session 隔离，请同时设置两个位置以保持
  // utils.getObject ↔ storage-helpers.getObject 行为一致。
  return (window as any)?._config?.storeSessionKey;
}

function prefixKey(key: string): string {
  const sessionKey = getStoreSessionKey();
  return sessionKey ? `${sessionKey}-${key}` : key;
}

export function getObject(key: string): any {
  return storage.getItem(prefixKey(key));
}

export function setObject(key: string, value: any): void {
  (globalThis as any)._cache = (globalThis as any)._cache || {};
  (globalThis as any)._cache[prefixKey(key)] = value;
  storage.setItem(prefixKey(key), value);
}

export function getTmpObject(key: string): any {
  (globalThis as any)._tmpcache = (globalThis as any)._tmpcache || {};
  if (!(globalThis as any)._tmpcache[key]) {
    (globalThis as any)._tmpcache[key] = seStorage.getItem(key);
  }
  return (globalThis as any)._tmpcache[key];
}

export function setTmpObject(key: string, value: any): void {
  (globalThis as any)._tmpcache = (globalThis as any)._tmpcache || {};
  (globalThis as any)._tmpcache[key] = value;
  seStorage.setItem(key, value);
}

export function getCookie(cname: string): string {
  const name = cname + "=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

export function setCookie(name: string, value: string, days: number): void {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${date.toUTCString()};path=/`;
}
